import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, LogOut, Trash2, AlertTriangle, Sparkles, ArrowRight } from 'lucide-react';
import { useSession } from '../context/SessionContext';
import { toast } from 'sonner';
import { getAuth, deleteUser } from 'firebase/auth';
import { isFeatureUnlocked } from '../services/iapService';
import { trackEvent, trackPageView } from '../services/analytics';
import useDynamicPrices from '../hooks/useDynamicPrices';
import { rlog } from '../utils/railwayLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const ProfileTab = () => {
  const navigate = useNavigate();
  const { isPremium, subscription, user, birthDetails: sessionBirthDetails, logout } = useSession();
  const { getDisplayPrice } = useDynamicPrices();

  // Analytics tracking
  useEffect(() => {
    trackPageView('profile');
    trackEvent('profile_view', { is_premium: isPremium });
    const versions = (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}'); } catch { return {}; } })();
    rlog('profile_mount', {
      is_premium: isPremium,
      plan_id: subscription?.plan_id,
      plan_tier: subscription?.plan_tier,
      credits: localStorage.getItem('sage_credits'),
      content_versions: versions,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [userData, setUserData] = useState({
    name: 'User',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    isUnlocked: false
  });
  
  useEffect(() => {
    const loadUserData = () => {
      try {
        let birthData = sessionBirthDetails;
        
        if (!birthData) {
          const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
          if (birthDetails) {
            birthData = JSON.parse(birthDetails);
          }
        }
        
        if (birthData) {
          let formattedDate = '';
          if (birthData.dateOfBirth) {
            formattedDate = birthData.dateOfBirth;
          } else if (birthData.day && birthData.month && birthData.year) {
            formattedDate = `${String(birthData.day).padStart(2, '0')}-${String(birthData.month).padStart(2, '0')}-${birthData.year}`;
          }
          
          let formattedTime = birthData.timeOfBirth || '';
          if (birthData.hour !== undefined) {
            const hour = birthData.hour > 12 ? birthData.hour - 12 : birthData.hour || 12;
            const period = birthData.hour >= 12 ? 'PM' : 'AM';
            formattedTime = `${String(hour).padStart(2, '0')}:${String(birthData.minute || 0).padStart(2, '0')} ${period}`;
          }
          
          let placeValue = birthData.place || birthData.placeOfBirth || birthData.placeName || birthData.pob || '';
          
          setUserData({
            name: birthData.name || user?.name || localStorage.getItem('userName') || 'User',
            dateOfBirth: formattedDate,
            timeOfBirth: formattedTime,
            placeOfBirth: placeValue,
            isUnlocked: isPremium
          });
        } else {
          setUserData({
            name: user?.name || localStorage.getItem('userName') || 'User',
            dateOfBirth: '',
            timeOfBirth: '',
            placeOfBirth: '',
            isUnlocked: isPremium
          });
        }
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    };
    
    loadUserData();
  }, [sessionBirthDetails, user, isPremium]);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  const [showNuclearConfirm, setShowNuclearConfirm] = useState(false);

  const handleClearCache = async () => {
    if (isClearingCache) return;
    setIsClearingCache(true);
    try {
      // Step 1: Wipe ALL MongoDB data (no filters — true nuclear)
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/clear-cache`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const data = await res.json();
        console.log('Nuclear reset result:', data);
      } catch (fetchErr) {
        console.log('Backend reset error (continuing):', fetchErr.message);
      }

      // Step 2: Delete Firebase user if logged in
      try {
        const auth = getAuth();
        if (auth && auth.currentUser) {
          await auth.signOut();
          console.log('Firebase signed out');
        }
      } catch (fbErr) {
        console.log('Firebase signout skipped:', fbErr.message);
      }

      // Step 3: Wipe ALL localStorage — nothing preserved
      try { localStorage.clear(); } catch(e) {}

      // Step 4: Clear sessionStorage too
      try { sessionStorage.clear(); } catch(e) {}

      toast.success('Nuclear reset done! Starting fresh...');

      // Step 5: Redirect to login — completely fresh start
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
    } catch (e) {
      console.error('Nuclear reset error:', e);
      // Even if something failed, clear local data and redirect
      try { localStorage.clear(); } catch(x) {}
      try { sessionStorage.clear(); } catch(x) {}
      toast.success('Reset done! Redirecting...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    // Don't await — logout() is now fire-and-forget (backend call), local state clears instantly
    logout();
    window.location.href = '/login';
  };

  const handleDeleteAccount = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    
    try {
      const userId = user?.user_id || localStorage.getItem('user_id');
      const deviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (!userId) {
        toast.error('User ID not found — please login again');
        setIsDeleting(false);
        return;
      }
      
      toast.loading('🔴 Wiping your data from servers...', { id: 'delete-toast' });
      
      // Step 1: Delete from MongoDB (backend) — hit both endpoints for safety
      let backendSuccess = false;
      let deletedCounts = null;
      let backendError = null;
      
      const endpoints = [
        `${BACKEND_URL}/api/auth/delete-account`,
        `${BACKEND_URL}/api/auth/nuclear-reset`,
      ];
      
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              user_id: userId,
              device_id: deviceId,
              firebase_uid: firebaseUser?.uid,
              confirm: true
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            backendSuccess = true;
            deletedCounts = data.deleted_counts;
            console.log(`✅ Backend wipe via ${endpoint}:`, data);
            break;
          } else {
            const errText = await response.text();
            backendError = `${response.status}: ${errText.slice(0, 200)}`;
            console.warn(`⚠️ ${endpoint} failed: ${backendError}`);
          }
        } catch (e) {
          backendError = e.message;
          console.warn(`⚠️ ${endpoint} network error:`, e.message);
        }
      }
      
      if (!backendSuccess) {
        toast.dismiss('delete-toast');
        // Still proceed with local wipe — warn user
        console.error('Backend delete failed, proceeding with local wipe only:', backendError);
        toast.warning('Server unreachable — clearing device data only. Retry later to wipe server data.');
      } else {
        const total = deletedCounts ? Object.values(deletedCounts).filter(v => typeof v === 'number').reduce((a,b) => a+b, 0) : 0;
        toast.success(`Server wiped: ${total} records deleted`, { id: 'delete-toast' });
      }
      
      // Step 2: Delete from Firebase Auth
      if (firebaseUser) {
        try {
          await deleteUser(firebaseUser);
          console.log('✅ Firebase user deleted');
        } catch (firebaseErr) {
          console.log('⚠️ Firebase delete skipped:', firebaseErr.message);
          // Common: requires-recent-login. Not fatal.
        }
      }
      
      // Step 3: COMPLETE client-side wipe (localStorage + sessionStorage + IndexedDB + Capacitor Preferences)
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.warn('Storage clear error:', e);
      }
      
      // Wipe IndexedDB
      try {
        if (window.indexedDB && typeof indexedDB.databases === 'function') {
          const dbs = await indexedDB.databases();
          await Promise.all((dbs || []).map(dbInfo => 
            new Promise(resolve => {
              try {
                const req = indexedDB.deleteDatabase(dbInfo.name);
                req.onsuccess = req.onerror = req.onblocked = () => resolve();
              } catch (_) { resolve(); }
            })
          ));
          console.log('✅ IndexedDB wiped');
        }
      } catch (e) {
        console.warn('IndexedDB wipe error:', e);
      }
      
      // Wipe Capacitor Preferences (native iOS/Android SharedPreferences/UserDefaults)
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.clear();
          console.log('✅ Capacitor Preferences wiped');
        }
      } catch (e) {
        console.warn('Preferences wipe error:', e);
      }
      
      // Wipe cookies (best-effort, same-origin)
      try {
        document.cookie.split(';').forEach(c => {
          const name = c.split('=')[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
      } catch (_) {}
      
      toast.success('✅ Account fully deleted. Starting fresh...');
      
      // Redirect to onboarding (full reload to reset React state)
      // After nuclear wipe, this lands on "Begin Your Journey" fresh flow
      setTimeout(() => {
        window.location.href = '/onboarding-new';
      }, 1500);
      
    } catch (error) {
      console.error('Delete account error:', error);
      toast.dismiss('delete-toast');
      toast.error(error.message || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  // Floating particles
  const particles = [...Array(15)].map((_, i) => ({
    id: i,
    size: Math.random() * 2 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    color: i % 3 === 0 ? '#92FFF7' : i % 3 === 1 ? '#74B3FF' : '#5F42C9'
  }));

  return (
    <div 
      data-testid="profile-page"
      style={{
        height: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
      }}
    >
      {/* Scrollable content container */}
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: '100px' }}>
      {/* Cosmic nebula */}
      <motion.div
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.06) 0%, rgba(138, 43, 226, 0.04) 50%, transparent 80%)',
          filter: 'blur(80px)',
          left: '50%',
          top: '20%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            left: `${p.x}%`,
            top: `${p.y}%`,
            pointerEvents: 'none',
          }}
          animate={{
            y: [0, -12, 0],
            opacity: [0.15, 0.4, 0.15],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 10, padding: 'clamp(16px, 3vh, 24px) clamp(14px, 4vw, 20px)', maxWidth: 'min(600px, 92vw)', margin: '0 auto' }}>
        
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            marginBottom: 'clamp(20px, 4vh, 32px)',
            paddingTop: 'calc(clamp(12px, 2vh, 20px) + env(safe-area-inset-top, 0px))',
          }}
        >
          {/* Avatar */}
          <div style={{
            width: 'clamp(56px, 14vw, 80px)',
            height: 'clamp(56px, 14vw, 80px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'clamp(10px, 2vh, 16px)',
            boxShadow: '0 8px 32px rgba(116, 179, 255, 0.3)',
            fontSize: 'clamp(22px, 6vw, 32px)',
            color: '#fff',
            fontWeight: 600,
          }}>
            {userData.name.charAt(0).toUpperCase()}
          </div>

          {/* Name */}
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(20px, 5.5vw, 26px)',
            fontWeight: 400,
            color: '#F3E5AB',
            marginBottom: '4px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
          }}>
            {userData.name}
          </h1>

          {/* Status Badge - Show actual plan tier */}
          {(() => {
            const hasFutureReels = isFeatureUnlocked('future_reels');
            const hasTimeline = isFeatureUnlocked('timeline');
            const planName = hasTimeline ? 'Ultimate' : hasFutureReels ? 'Premium' : userData.isUnlocked ? 'Starter' : 'Free';
            const planColor = hasTimeline ? '#f59e0b' : hasFutureReels ? '#92FFF7' : userData.isUnlocked ? '#a78bfa' : 'rgba(226, 232, 240, 0.5)';
            return (
              <span style={{
                fontSize: 'clamp(9px, 2.5vw, 11px)',
                fontWeight: 500,
                color: planColor,
                background: `${planColor}15`,
                padding: 'clamp(4px, 1vh, 6px) clamp(10px, 3vw, 14px)',
                borderRadius: '20px',
                border: `1px solid ${planColor}30`,
              }}
              data-testid="plan-badge"
              >
                {planName === 'Ultimate' && '★ '}{planName} {userData.isUnlocked ? 'Plan' : 'Account'}
              </span>
            );
          })()}
        </motion.div>

        {/* Upgrade Card - For free users AND Starter users */}
        {(() => {
          const hasFutureReels = isFeatureUnlocked('future_reels');
          const hasTimeline = isFeatureUnlocked('timeline');
          // Show upgrade for: free users, Starter (no future_reels), Premium (no timeline)
          if (hasTimeline) return null; // Ultimate - no upgrade needed
          
          const isStarter = userData.isUnlocked && !hasFutureReels;
          const isPremiumPlan = userData.isUnlocked && hasFutureReels && !hasTimeline;
          
          const upgradeTitle = !userData.isUnlocked 
            ? 'Unlock Premium Features' 
            : isStarter 
              ? 'Upgrade to Premium' 
              : 'Upgrade to Ultimate';
          const upgradeDesc = !userData.isUnlocked 
            ? 'Get Life Insights, Future Reels & more'
            : isStarter 
              ? 'Unlock Future Reels — 8 domain predictions'
              : 'Full Timeline Access & unlimited features';
          const upgradePrice = (() => {
            const pkgId = !userData.isUnlocked ? 'starter' : isStarter ? 'premium' : 'ultimate';
            const price = getDisplayPrice(pkgId);
            return price ? `${price}/mo` : '';
          })();
          const gradFrom = isStarter ? '#f59e0b' : isPremiumPlan ? '#a78bfa' : '#74B3FF';
          const gradTo = isStarter ? '#d97706' : isPremiumPlan ? '#7c3aed' : '#5F42C9';
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onClick={() => {
                const min = !userData.isUnlocked ? 199 : isStarter ? 599 : 999;
                navigate(`/payment?min=${min}&source=upgrade`);
              }}
              data-testid="profile-upgrade-card"
              style={{
                background: `linear-gradient(135deg, ${gradFrom}12, ${gradTo}08)`,
                border: `1px solid ${gradFrom}30`,
                borderRadius: 'clamp(10px, 3vw, 14px)',
                padding: 'clamp(12px, 2.5vh, 16px)',
                marginBottom: 'clamp(16px, 3vh, 24px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                  <p style={{ 
                    fontSize: 'clamp(12px, 3.2vw, 14px)', 
                    fontWeight: 600, 
                    color: '#E2E8F0',
                  }}>
                    {upgradeTitle}
                  </p>
                </div>
                <p style={{ 
                  fontSize: 'clamp(10px, 2.8vw, 12px)', 
                  color: 'rgba(226, 232, 240, 0.5)',
                }}>
                  {upgradeDesc}
                </p>
              </div>
              <button style={{
                background: `linear-gradient(135deg, ${gradFrom} 0%, ${gradTo} 100%)`,
                border: 'none',
                borderRadius: 'clamp(6px, 2vw, 10px)',
                padding: 'clamp(8px, 1.5vh, 10px) clamp(12px, 3vw, 18px)',
                color: '#fff',
                fontSize: 'clamp(10px, 2.8vw, 12px)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px',
                flexShrink: 0,
              }}>
                {upgradePrice || 'View Plans'}
                <ArrowRight style={{ width: '12px', height: '12px' }} />
              </button>
            </motion.div>
          );
        })()}

        {/* Subscription Info - For premium users */}
        {userData.isUnlocked && subscription && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              background: 'rgba(146, 255, 247, 0.05)',
              border: '1px solid rgba(146, 255, 247, 0.15)',
              borderRadius: 'clamp(10px, 2.5vw, 14px)',
              padding: 'clamp(10px, 2.5vw, 16px)',
              marginBottom: 'clamp(14px, 3vh, 24px)',
            }}
            data-testid="subscription-info"
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <p style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: 600, color: '#92FFF7' }}>
                {subscription.plan_name || (() => {
                  const hasFR = isFeatureUnlocked('future_reels');
                  const hasTL = isFeatureUnlocked('timeline');
                  return hasTL ? 'Ultimate' : hasFR ? 'Premium' : 'Starter';
                })()}
              </p>
              <span style={{ 
                fontSize: 'clamp(10px, 2.5vw, 11px)', 
                color: 'rgba(226, 232, 240, 0.5)',
              }}>
                {subscription.is_recurring ? 'Monthly' : 'One-time'}
              </span>
            </div>
            <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(226, 232, 240, 0.4)' }}>
              {subscription.is_recurring ? 'Renews' : 'Expires'}: {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'N/A'}
            </p>
          </motion.div>
        )}

        {/* Birth Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          style={{ marginBottom: 'clamp(14px, 3vh, 24px)' }}
        >
          <p style={{ 
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(12px, 3vw, 14px)', 
            fontWeight: 400, 
            color: '#F3E5AB',
            letterSpacing: '0.25em',
            marginBottom: '12px',
            textTransform: 'uppercase',
          }}>
            Birth Details
          </p>
          
          <div style={{
            background: 'rgba(116, 179, 255, 0.04)',
            border: '1px solid rgba(116, 179, 255, 0.1)',
            borderRadius: 'clamp(10px, 2.5vw, 14px)',
            overflow: 'hidden',
          }}>
            {/* Date */}
            <div style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(116, 179, 255, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(226, 232, 240, 0.5)' }}>Date of Birth</span>
              <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: '#E2E8F0', fontWeight: 500 }}>
                {userData.dateOfBirth || 'Not set'}
              </span>
            </div>
            
            {/* Time */}
            <div style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(116, 179, 255, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(226, 232, 240, 0.5)' }}>Time of Birth</span>
              <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: '#E2E8F0', fontWeight: 500 }}>
                {userData.timeOfBirth || 'Not set'}
              </span>
            </div>
            
            {/* Place */}
            <div style={{
              padding: '14px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(226, 232, 240, 0.5)' }}>Place of Birth</span>
              <span style={{ 
                fontSize: 'clamp(11px, 3vw, 13px)', 
                color: '#E2E8F0', 
                fontWeight: 500,
                maxWidth: '180px',
                textAlign: 'right',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {userData.placeOfBirth || 'Not set'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ marginBottom: 'clamp(14px, 3vh, 24px)' }}
        >
          <p style={{ 
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(12px, 3vw, 14px)', 
            fontWeight: 400, 
            color: '#F3E5AB',
            letterSpacing: '0.25em',
            marginBottom: '12px',
            textTransform: 'uppercase',
          }}>
            Settings
          </p>
          
          <div style={{
            background: 'rgba(116, 179, 255, 0.04)',
            border: '1px solid rgba(116, 179, 255, 0.1)',
            borderRadius: 'clamp(10px, 2.5vw, 14px)',
          }}>
            <button
              onClick={() => navigate('/privacy-policy')}
              data-testid="privacy-policy-profile-btn"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: '#E2E8F0' }}>Privacy Policy</span>
              <ChevronRight size={16} style={{ color: 'rgba(226, 232, 240, 0.3)' }} />
            </button>
            <button
              onClick={() => navigate('/terms-of-service')}
              data-testid="terms-of-service-profile-btn"
              style={{
                width: '100%',
                padding: 'clamp(10px, 2.5vw, 14px) clamp(14px, 3.5vw, 18px)',
                background: 'transparent',
                border: 'none',
                borderTop: '1px solid rgba(116, 179, 255, 0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: '#E2E8F0' }}>Terms of Service</span>
              <ChevronRight size={16} style={{ color: 'rgba(226, 232, 240, 0.3)' }} />
            </button>
          </div>
        </motion.div>

        {/* Logout Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleLogout}
          disabled={isLoggingOut}
          data-testid="logout-btn"
          style={{
            width: '100%',
            padding: 'clamp(10px, 2.5vw, 14px)',
            background: isLoggingOut ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: 'clamp(8px, 2vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: isLoggingOut ? 'wait' : 'pointer',
            marginBottom: 'max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))',
            opacity: isLoggingOut ? 0.7 : 1,
          }}
        >
          <LogOut size={16} style={{ color: '#ef4444' }} />
          <span style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: 500, color: '#ef4444' }}>
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </span>
        </motion.button>

        {/* Delete Account Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowDeleteConfirm(true)}
          data-testid="delete-account-btn"
          style={{
            width: '100%',
            padding: 'clamp(10px, 2.5vw, 14px)',
            background: 'transparent',
            border: '1px solid rgba(239, 68, 68, 0.15)',
            borderRadius: 'clamp(8px, 2vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            marginBottom: 'clamp(14px, 3vh, 24px)',
          }}
        >
          <Trash2 size={14} style={{ color: 'rgba(239, 68, 68, 0.6)' }} />
          <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: 500, color: 'rgba(239, 68, 68, 0.6)' }}>
            Delete Account
          </span>
        </motion.button>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 'clamp(12px, 3vw, 20px)',
            }}
            onClick={() => !isDeleting && setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(145deg, #1a1030 0%, #0f0a1e 100%)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '20px',
                padding: '24px',
                maxWidth: '340px',
                width: '100%',
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: 'clamp(12px, 2.5vh, 20px)' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                </div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 600, 
                  color: '#E2E8F0',
                  marginBottom: '8px',
                }}>
                  Delete Account?
                </h3>
                <p style={{ 
                  fontSize: 'clamp(11px, 3vw, 13px)', 
                  color: 'rgba(226, 232, 240, 0.6)',
                  lineHeight: 1.5,
                }}>
                  This will permanently delete all your data including birth details, stories, and subscription. This cannot be undone.
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    padding: 'clamp(8px, 2vw, 12px)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '10px',
                    color: '#E2E8F0',
                    fontSize: 'clamp(12px, 3.2vw, 14px)',
                    fontWeight: 500,
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    padding: 'clamp(8px, 2vw, 12px)',
                    background: isDeleting ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '10px',
                    color: '#ef4444',
                    fontSize: 'clamp(12px, 3.2vw, 14px)',
                    fontWeight: 600,
                    cursor: isDeleting ? 'wait' : 'pointer',
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Version */}
        <p style={{
          textAlign: 'center',
          fontSize: 'clamp(9px, 2.2vw, 10px)',
          color: 'rgba(226, 232, 240, 0.2)',
          letterSpacing: '1px',
        }}>
          taraanaya v1.0.0
        </p>
      </div>
      </div>
    </div>
  );
};

export default ProfileTab;
